/**
 * The gallery grid — one neon card per game. Clicking the card face
 * launches; the TOP 10 chip opens the leaderboard modal.
 */

import { CATALOG, type CatalogEntry } from '../catalog.js';
import { track } from '../runtime/analytics.js';
import { openLeaderboard } from './leaderboard.js';
import { launchGame, type PlaySession } from './play.js';
import { resolvePlayAccess } from './play-access.js';
import { consumePendingLaunch, paywallGrantsPlay, showLoginWall, showPaywall } from './play-gate-wall.js';
import { refreshFooterCredits } from './footer-credit.js';
import { playCountFor } from './plays.js';

let activeSession: PlaySession | null = null;
// Synchronous "a launch is in flight" latch (sc-7064). activeSession alone
// can't guard concurrency because it isn't set until the ASYNC gate
// (resolvePlayAccess → network) resolves — a fast double-click re-enters
// launch() while the first gate promise is still pending, both see
// activeSession===null, and both proceed → double token spend / two
// sessions. This flag is set SYNCHRONOUSLY the moment a launch begins and
// released when the gate ends in a non-play verdict, the session closes,
// or the gate throws.
let launching = false;

const NEW_ROW_SIZE = 3;

/**
 * Gallery order: the 3 newest titles hold the front row (by `released`
 * date), then everything else ranks by play count — GLOBAL counts when
 * the API has ever answered, this device's ledger otherwise (plays.ts).
 * Ties fall back to the classic marquee lineup (catalog order).
 */
function orderedCatalog(): { entry: CatalogEntry; isNew: boolean }[] {
  const byLineup = new Map(CATALOG.map((e, i) => [e.id, i]));
  const newest = [...CATALOG]
    .sort(
      (a, b) =>
        b.released.localeCompare(a.released) ||
        (byLineup.get(a.id) ?? 0) - (byLineup.get(b.id) ?? 0)
    )
    .slice(0, NEW_ROW_SIZE);
  const newIds = new Set(newest.map((e) => e.id));
  const rest = CATALOG.filter((e) => !newIds.has(e.id)).sort(
    (a, b) =>
      playCountFor(b.id) - playCountFor(a.id) ||
      (byLineup.get(a.id) ?? 0) - (byLineup.get(b.id) ?? 0)
  );
  return [
    ...newest.map((entry) => ({ entry, isNew: true })),
    ...rest.map((entry) => ({ entry, isNew: false })),
  ];
}

export function renderGrid(): void {
  const grid = document.getElementById('gallery-grid');
  if (!(grid instanceof HTMLElement)) {
    throw new Error('gallery grid DOM missing — index.html is broken');
  }
  grid.textContent = '';

  orderedCatalog().forEach(({ entry, isNew }, i) => {
    grid.appendChild(buildCard(entry, i, isNew));
  });
}

function buildCard(entry: CatalogEntry, index: number, isNew: boolean): HTMLElement {
  const card = document.createElement('article');
  card.className = 'card';
  card.style.setProperty('--card-accent', entry.accent);
  card.style.setProperty('--card-accent2', entry.accent2);
  card.style.setProperty('--card-i', String(index));

  const band = document.createElement('div');
  band.className = 'card__band';

  if (isNew) {
    const chip = document.createElement('span');
    chip.className = 'card__new';
    chip.textContent = 'NEW';
    card.appendChild(chip);
  }

  // the cabinet screen — a CSS mini-scene unique to each game
  const scene = document.createElement('div');
  scene.className = 'card__scene';
  scene.dataset.scene = entry.id;
  for (let s = 1; s <= 3; s++) {
    const el = document.createElement('i');
    el.className = `sc sc${s}`;
    scene.appendChild(el);
  }
  const glare = document.createElement('span');
  glare.className = 'card__glare';
  scene.appendChild(glare);

  const title = document.createElement('h2');
  title.className = 'card__title';
  title.textContent = entry.title;

  const genre = document.createElement('span');
  genre.className = 'card__genre';
  genre.textContent = `${entry.genre} · 1984`;

  const blurb = document.createElement('p');
  blurb.className = 'card__blurb';
  blurb.textContent = entry.blurb;

  const actions = document.createElement('div');
  actions.className = 'card__actions';

  const playBtn = document.createElement('button');
  playBtn.type = 'button';
  playBtn.className = 'card__play';
  playBtn.textContent = '▶ INSERT COIN';
  playBtn.addEventListener('click', () => launch(entry));

  actions.appendChild(playBtn);

  if (!entry.scoreless) {
    const boardBtn = document.createElement('button');
    boardBtn.type = 'button';
    boardBtn.className = 'card__board';
    boardBtn.textContent = 'TOP 10';
    boardBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      openLeaderboard(entry);
    });
    actions.appendChild(boardBtn);
  }

  card.append(band, scene, title, genre, blurb, actions);
  return card;
}

function launch(entry: CatalogEntry): void {
  track('cartridge_selected', { cartridgeId: entry.id, remastered: true });
  beginLaunch(entry);
}

/**
 * The single latched entry point for every launch (sc-7064). Acquires the
 * `launching` latch SYNCHRONOUSLY — a second call while a launch is in
 * flight (session active OR gate pending) is a no-op, so a double-click
 * can't fire two gates / spend two tokens. All launch triggers route
 * through here: the card button, the in-place email-OTP relaunch, and the
 * post-callback relaunchPending.
 */
function beginLaunch(entry: CatalogEntry): void {
  if (activeSession || launching) return; // one cartridge at a time
  launching = true;
  void gateThenLaunch(entry).catch(() => {
    // Defensive: resolvePlayAccess is written not to throw, but never let
    // an unexpected rejection strand the latch (which would wedge the
    // cabinet — no further launches possible).
    launching = false;
  });
}

/**
 * The entry gate (sc-6940): resolve play access, THEN mount — or show the
 * login wall / paywall. A signed-in token play spends the token at run
 * start (sc-7036). Assumes the `launching` latch is already held (set by
 * beginLaunch); releases it on every non-play terminal branch, and hands
 * it to startSession on 'play' (session ownership → activeSession).
 */
async function gateThenLaunch(entry: CatalogEntry): Promise<void> {
  const verdict = await resolvePlayAccess();
  switch (verdict.grant) {
    case 'play':
      // access tells the session whether a token must be spent at run
      // START (sc-7036): 'token' ⇒ spend on onStarted; 'pass'/'free-taste'
      // ⇒ no token spend (a pass is unlimited; the anon taste was already
      // consumed at the gate). startSession takes over the latch.
      startSession(entry, verdict.access);
      break;
    case 'login':
      track('login_wall_shown', { cartridgeId: entry.id });
      // Wall is up — the launch attempt is over; release the latch so a
      // dismiss-and-retry works. onSignedIn (in-place email-OTP, sc-7039)
      // re-enters through beginLaunch, re-acquiring cleanly. Google/magic-
      // link leave the page and resume via /auth/callback + relaunchPending.
      launching = false;
      void showLoginWall(entry.id, entry.title, () => {
        beginLaunch(entry);
      });
      break;
    case 'paywall':
      track('paywall_shown', { cartridgeId: entry.id });
      launching = false;
      // Pass the cartridge so a purchase relaunches it via /billing/return.
      // A rewarded token earned in the wall resolves tokenEarned → relaunch
      // the run right here (the run's own start-spend consumes the token).
      void showPaywall(verdict.resetsAtUtc, entry.id).then((r) => {
        if (paywallGrantsPlay(r)) beginLaunch(entry);
      });
      break;
    case 'error':
      // Server said no (fail-closed) — never a free run. Treat as paywall
      // so the player sees "try again shortly" rather than a silent nothing.
      launching = false;
      void showPaywall(undefined, entry.id).then((r) => {
        if (paywallGrantsPlay(r)) beginLaunch(entry);
      });
      break;
  }
}

function startSession(entry: CatalogEntry, access: 'pass' | 'token' | 'free-taste'): void {
  // Latch handoff (sc-7064): beginLaunch holds `launching` across the async
  // gate; here the session becomes the guard. Set activeSession first, then
  // release the latch — never a window where both are false while a run
  // exists.
  if (activeSession) {
    launching = false;
    return;
  }
  activeSession = launchGame(entry, {
    // Only a 'token' run spends at start (sc-7036). A pass is unlimited;
    // the anon free-taste was already consumed at the gate.
    spendTokenOnStart: access === 'token',
    onClose: () => {
      activeSession = null;
      // play counts changed — let the lineup re-rank while the player
      // is looking at the gallery anyway
      renderGrid();
      // a token run just ended (or a pass lapsed) — repaint the footer
      // credit so the count reflects the spend.
      void refreshFooterCredits();
    },
  });
  launching = false;
}

/**
 * Auto-relaunch after an auth round-trip (sc-6940). main.ts calls this on
 * boot when the callback signed the player in and a pending cartridge was
 * stashed before the redirect. The player lands back mid-flow, in the game
 * they were trying to coin.
 */
export function relaunchPending(): void {
  const id = consumePendingLaunch();
  if (!id) return;
  const entry = CATALOG.find((c) => c.id === id);
  if (entry) beginLaunch(entry);
}
